// 📱 실행 플랫폼 판정 (2026-08-16) — 데스크톱은 desktop/isTauri(), 안드로이드 앱은 여기.
//   APK(.ops/android-webview)가 addJavascriptInterface 로 window.TPTAndroid 를 주입한다.
//   "앱에서는 숨김/다르게" 분기가 여러 화면에 흩어져 인라인으로 반복되던 것을 한 곳으로 모음.
import { isTauri } from '../desktop/env';

export function isAndroidApp(): boolean {
	return typeof window !== 'undefined' && !!(window as { TPTAndroid?: unknown }).TPTAndroid;
}

// 🍎 iOS 앱 판정 (2026-08-19) — WKWebView 셸이 WKScriptMessageHandler 를 등록하면
//   window.webkit.messageHandlers 가 생긴다. 일반 Safari·크롬에는 이 객체가 없다.
//   ⚠️ 브릿지 '이름'을 보지 않는다 — iOS 쪽 핸들러 명칭이 바뀌어도 판정이 깨지지 않게 존재 여부만 본다
//     (안드로이드는 window.TPTAndroid 라는 이름 자체에 묶여 있어 대칭이 아니다).
//   데스크톱(Tauri)은 Windows 전용이라 WebView2 를 쓰므로 여기 걸리지 않는다.
export function isIosApp(): boolean {
	if (typeof window === 'undefined') return false;
	// WKWebView 셸이 WKScriptMessageHandler 를 등록하면 이 객체가 생긴다. 일반 Safari·크롬에는 없다.
	//   🔴 iOS 셸은 브릿지를 **안드로이드와 같은 이름**(window.TPTAndroid)으로 주입한다 (2026-08-20 확인).
	//     그래서 isAndroidApp() 도 iOS 에서 참이 된다 — 웹 42곳이 이름 하나만 알면 되게 한 의도된 설계다.
	//     "안드로이드만" / "iOS만" 을 갈라야 할 때만 이 함수를 쓴다.
	//   초기엔 밑줄 접두 전역도 브릿지로 오해해 함께 봤는데, 그것은 동기 반환용 내부 저장소였다 —
	//     죽은 참조라 제거했다(2026-08-20).
	const w = window as { webkit?: { messageHandlers?: unknown } };
	return !!w.webkit?.messageHandlers;
}

// 📲 네이티브 브릿지 객체 — 안드로이드 APK 와 iOS 셸이 **같은 이름**(window.TPTAndroid)으로 주입한다.
//   호출부가 전역 이름을 직접 알지 않게 하는 단일 지점(계약은 api/notifications.ts 의 declare global).
//   iOS 는 14개만 노출한다 — updateApp·getVersionCode 제외(App Store 가 자체 업데이트를 금지하므로
//   '능력 없음'을 그대로 알린다. no-op 로 노출하면 MobileNav 의 기능 탐지가 속아 업데이트 UI 가 뜬다).
//   전부 옵셔널이라 호출부는 수정 없이 그대로 동작한다.
export function nativeBridge(): Window['TPTAndroid'] {
	if (typeof window === 'undefined') return undefined;
	return window.TPTAndroid;
}

// 네이티브 앱(안드로이드 APK · iOS) 공통 판정 — "앱에서는 숨김/다르게" 분기에 쓴다.
export function isNativeApp(): boolean {
	return isAndroidApp() || isIosApp();
}

// 🖥 실행 플랫폼을 html[data-platform] 으로 노출 (2026-08-26 사용자 — "웹 글자가 데탑보다 너무 진하다").
//   데스크톱(Tauri/WebView2)은 글자를 그레이스케일 안티앨리어싱으로 그려 같은 색도 얇고 옅게 보이는데,
//   브라우저·앱 웹뷰는 서브픽셀 렌더링이라 같은 먹색이 훨씬 진하게 찍힌다. 색으로 그 차이를 메우려면
//   CSS 가 실행 환경을 알아야 하므로 부팅 시 한 번 속성으로 박는다(index.css 의 플랫폼 톤 보정 블록).
//   'desktop' | 'app'(안드/iOS 네이티브 셸) | 'web'(브라우저·홈화면 웹앱).
export function applyPlatformAttr(): void {
	if (typeof document === 'undefined') return;
	const p = isTauri() ? 'desktop' : isNativeApp() ? 'app' : 'web';
	document.documentElement.setAttribute('data-platform', p);
}
